import { parseDate, parseInstant, formatDate, fromUnixMilliseconds, Temporal } from 'shared-components';
import { months } from 'platform-data';
/**
 * Parses various date formats into a Temporal.PlainDate
 * Handles:
 * - Unix timestamps (as numbers or strings)
 * - DD/MM/YYYY format strings
 * - ISO date strings (YYYY-MM-DD)
 * - Temporal objects
 */
export function parseDateFlexible(input: string | number | null | undefined): Temporal.PlainDate | null {
  if (!input && input !== 0) return null;

  try {
    // Handle numbers (unix timestamps in milliseconds)
    if (typeof input === 'number') {
      const instant = fromUnixMilliseconds(input);
      return instant.toZonedDateTimeISO('UTC').toPlainDate();
    }

    const inputStr = String(input).trim();
    if (!inputStr) return null;

    // Check if it's a DD/MM/YYYY format
    if (inputStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const [day, month, year] = inputStr.split('/');
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      return parseDate(isoDate);
    }

    // Check if it's likely a unix timestamp string
    if (inputStr.match(/^\d{10,13}$/)) {
      const timestamp = parseInt(inputStr);
      if (!isNaN(timestamp)) {
        // If it's 10 digits, it's seconds; if 13, it's milliseconds
        const milliseconds = inputStr.length === 10 ? timestamp * 1000 : timestamp;
        const instant = fromUnixMilliseconds(milliseconds);
        return instant.toZonedDateTimeISO('UTC').toPlainDate();
      }
    }

    // Try parsing as ISO date or other supported formats
    return parseDate(inputStr);
  } catch (error) {
    console.warn('Date parsing failed for input:', input, error);
    return null;
  }
}

/**
 * Parses various date formats into a Temporal.PlainDateTime
 * Handles all the same formats as parseDateFlexible but returns PlainDateTime
 */
export function parseDateTimeFlexible(input: string | number | null | undefined): Temporal.PlainDateTime | null {
  if (!input && input !== 0) return null;

  try {
    // Handle numbers (unix timestamps in milliseconds)
    if (typeof input === 'number') {
      const instant = fromUnixMilliseconds(input);
      return instant.toZonedDateTimeISO('UTC').toPlainDateTime();
    }

    const inputStr = String(input).trim();
    if (!inputStr) return null;

    // Check if it's likely a unix timestamp string
    if (inputStr.match(/^\d{10,13}$/)) {
      const timestamp = parseInt(inputStr);
      if (!isNaN(timestamp)) {
        // If it's 10 digits, it's seconds; if 13, it's milliseconds
        const milliseconds = inputStr.length === 10 ? timestamp * 1000 : timestamp;
        const instant = fromUnixMilliseconds(milliseconds);
        return instant.toZonedDateTimeISO('UTC').toPlainDateTime();
      }
    }

    // For DD/MM/YYYY format, return start of day
    if (inputStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const plainDate = parseDateFlexible(inputStr);
      return plainDate ? plainDate.toPlainDateTime({ hour: 0, minute: 0, second: 0 }) : null;
    }

    // Try parsing as ISO datetime or date
    const plainDate = parseDate(inputStr);
    return plainDate ? plainDate.toPlainDateTime({ hour: 0, minute: 0, second: 0 }) : null;
  } catch (error) {
    console.warn('DateTime parsing failed for input:', input, error);
    return null;
  }
}

/**
 * Formats a date with fallback to original value if parsing fails
 */
export function formatDateSafe(
  input: string | number | null | undefined,
  format: string,
  fallback: string = '',
): string {
  const parsed = parseDateFlexible(input);
  return parsed ? formatDate(parsed, format) : fallback;
}

/**
 * Formats a Temporal.PlainDateTime according to the specified format string
 * Supports both date and time components
 */
function formatTemporalDateTime(dateTime: Temporal.PlainDateTime, format: string): string {
  // Month names array to match shared-components behavior (Sept instead of Sep)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

  // Handle time-only formats
  if (format === 'HH:mm') {
    const hours = dateTime.hour.toString().padStart(2, '0');
    const minutes = dateTime.minute.toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // Handle datetime formats
  if (format === 'DD MMM YYYY HH:mm') {
    const day = dateTime.day.toString().padStart(2, '0');
    const monthName = monthNames[dateTime.month - 1];
    const { year } = dateTime;
    const hours = dateTime.hour.toString().padStart(2, '0');
    const minutes = dateTime.minute.toString().padStart(2, '0');
    return `${day} ${monthName} ${year} ${hours}:${minutes}`;
  }

  // Handle other datetime formats if needed
  if (format === 'DD/MM/YYYY HH:mm') {
    const day = dateTime.day.toString().padStart(2, '0');
    const month = dateTime.month.toString().padStart(2, '0');
    const { year } = dateTime;
    const hours = dateTime.hour.toString().padStart(2, '0');
    const minutes = dateTime.minute.toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }

  // Fallback to date-only format if no time format is recognized
  return formatDate(dateTime.toPlainDate(), format);
}

/**
 * Formats a datetime with fallback to original value if parsing fails
 */
export function formatDateTimeSafe(
  input: string | number | null | undefined,
  format: string,
  fallback: string = '',
): string {
  const parsed = parseDateTimeFlexible(input);
  return parsed ? formatTemporalDateTime(parsed, format) : fallback;
}

/**
 * Formats an ISO instant string as a locale date-time string in en-GB format.
 * Output example: "20 Feb 2026, 14:30"
 */
export function formatInstantToLocaleDateTime(dateString: string | undefined | null): string {
  if (!dateString) return 'Invalid date';
  const instant = parseInstant(dateString);
  if (!instant) return 'Invalid date';
  return instant.toZonedDateTimeISO('UTC').toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

// date functions used in student-apply
export const formatYearMonthDate = (date: string, isShortened?: boolean): string => {
  if (!date) return undefined;

  const [YYYY, MM] = date.split('-');
  const month = months.find(m => m.value === MM);
  let monthName = month?.name ?? '';
  if (monthName === 'None') monthName = '';

  if (!monthName) return `${YYYY}`;
  if (isShortened) monthName = monthName.substring(0, 3);

  return `${monthName} ${YYYY}`;
};
